import { FiGlobe } from "react-icons/fi";
import {
  FaFlag,
  FaGlobeAmericas,
  FaGlobeEurope,
  FaGlobeAsia,
} from "react-icons/fa";

/**
 * Single source of truth for the market selector.
 *
 * This map used to be declared inline inside RegionSelector, which meant the
 * compact picker in the composer would have needed its own copy and the two
 * would have drifted the first time a country was added. Both import it now.
 */
export const regionData = {
  usa: {
    name: "North America",
    shortName: "N. America",
    icon: FaGlobeAmericas,
    countries: [{ id: "United States", name: "United States", icon: FaFlag }],
  },
  eu: {
    name: "Europe & UK",
    shortName: "Europe & UK",
    icon: FaGlobeEurope,
    countries: [
      { id: "United Kingdom", name: "United Kingdom", icon: FaFlag },
      { id: "Ireland", name: "Ireland", icon: FaFlag },
      { id: "France", name: "France", icon: FaFlag },
      { id: "Spain", name: "Spain", icon: FaFlag },
      { id: "Italy", name: "Italy", icon: FaFlag },
      { id: "Germany", name: "Germany", icon: FaFlag },
    ],
  },
  apac: {
    name: "APAC",
    shortName: "APAC",
    icon: FaGlobeAsia,
    countries: [
      { id: "Australia", name: "Australia", icon: FaFlag },
      { id: "New Zealand", name: "New Zealand", icon: FaFlag },
      { id: "Malaysia", name: "Malaysia", icon: FaFlag },
    ],
  },
  br: {
    name: "Brazil",
    shortName: "Brazil",
    icon: FaGlobeAmericas,
    countries: [{ id: "Brazil", name: "Brazil", icon: FaFlag }],
  },
  global: {
    name: "Global",
    shortName: "Global",
    icon: FiGlobe,
    countries: [{ id: "Global", name: "Global", icon: FiGlobe }],
  },
};

/**
 * Human-readable market for a region/country pair, used both for the picker
 * label and for the chip stamped onto each question in the transcript.
 */
export const marketLabel = (regionId, countryId) => {
  const region = regionData[regionId];
  if (!region) return "Global";
  const country = region.countries.find((c) => c.id === countryId);
  if (country) return country.name;
  return region.shortName || region.name;
};

export const marketIcon = (regionId, countryId) => {
  const region = regionData[regionId];
  if (!region) return FiGlobe;
  const country = region.countries.find((c) => c.id === countryId);
  return country?.icon || region.icon;
};

/**
 * Does this country actually belong to this region?
 *
 * A region/country pair can otherwise go out of step, and the failure is
 * quiet in a way that costs real trust: marketLabel above falls back to the
 * REGION name when the country is not found in it, so the transcript chip
 * reads "APAC" while the request still carries "Brazil". The user sees an
 * APAC-stamped question answered about Brazil.
 */
export const countryInRegion = (regionId, countryId) =>
  Boolean(regionData[regionId]?.countries.some((c) => c.id === countryId));

/**
 * First country of a region, used whenever a pair has to be repaired.
 *
 * Derived from regionData rather than kept as its own map. There used to be a
 * separate REGION_DEFAULT_COUNTRY literal in ChatComponent holding the same
 * five answers, which is one more place to forget when a market is added.
 */
export const defaultCountryForRegion = (regionId) =>
  regionData[regionId]?.countries[0]?.id ?? "Global";
